/**
 * Display vocabulary and shared read-state chrome for the UCC pages on
 * platform.aaim.com (Aaim-operator scope).
 *
 * Each UCC page carried its own status switch, and they disagreed with the
 * canonical enums: the Dashboard and Filings pages counted and filtered on
 * `filed` and `pending_submission`, which FilingStatus in
 * schema-registry/ucc-filing-lifecycle.openapi.yaml does not contain, never
 * counted `active`, and printed raw codes (`ucc1`, `pending_submission`) in
 * their badges. Every map below is keyed by a GENERATED enum type, so a value
 * added to a spec fails tsc here until it has a label. A value outside the
 * enum prints "Unrecognized" with the received code in the title attribute,
 * never the raw code and never a guessed label.
 *
 * Badge variants use success, warning, error and neutral only: the Badge
 * primitive's `info` variant renders in the accent red, which the design
 * canon reserves for CTAs and alerts.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  BodyText,
  Button,
  DataGrid,
  EmptyState,
  METRIC_READ_CAPTIONS,
  MetricCard,
  Skeleton,
  SkeletonTable,
} from '@aaimplatform/ui';
import type { ColumnDef, PaginationState } from '@aaimplatform/ui';
import { RefreshCw as RefreshIcon } from 'lucide-react';
import type { components as LifecycleComponents } from '@aaimplatform/institutional-types/ucc-filing-lifecycle';
import type { components as AnalyticsComponents } from '@aaimplatform/institutional-types/ucc-filing-analytics';
import type { components as FeeComponents } from '@aaimplatform/institutional-types/ucc-fee-reconciliation';
import type { components as SubmissionComponents } from '@aaimplatform/institutional-types/ucc-filing-submission';
import type { components as PaperComponents } from '@aaimplatform/institutional-types/ucc-paper-filing';
import type { components as AuditComponents } from '@aaimplatform/institutional-types/ucc-filing-audit-compliance';
import type { components as BulkComponents } from '@aaimplatform/institutional-types/ucc-bulk-operations';
import type { components as SearchComponents } from '@aaimplatform/institutional-types/ucc-search';
import type { components as JurisdictionComponents } from '@aaimplatform/institutional-types/ucc-jurisdiction-rules';
import { usePerfectionStatus, useTenants } from '../../hooks';
import { ABSENT, formatCount, formatDateTime } from '../../utils/display-format';

// ---------------------------------------------------------------------------
// Generated enum types
// ---------------------------------------------------------------------------

export type FilingStatus = LifecycleComponents['schemas']['FilingStatus'];
export type FilingType = LifecycleComponents['schemas']['FilingType'];
export type UccFiling = LifecycleComponents['schemas']['UccFiling'];
export type PortfolioPerfectionStatus = AnalyticsComponents['schemas']['PortfolioPerfectionStatus'];
export type FeePaymentStatus = FeeComponents['schemas']['FeePaymentStatus'];
export type SubmissionStatus = SubmissionComponents['schemas']['SubmissionStatus'];
export type PaperFilingStatus = PaperComponents['schemas']['PaperFilingStatus'];
export type AuditEventType = AuditComponents['schemas']['AuditEventType'];
export type AuditActorType = AuditComponents['schemas']['ActorType'];
export type ComplianceReportType = AuditComponents['schemas']['ComplianceReportType'];
export type BulkOperationType = BulkComponents['schemas']['BulkOperationType'];
export type BulkOperationStatus = BulkComponents['schemas']['BulkOperationStatus'];
export type SearchLogicType = SearchComponents['schemas']['SearchLogicType'];
export type SubmissionMethod = JurisdictionComponents['schemas']['SubmissionMethod'];
/** The jurisdiction-rules fee FilingType (UCC1, UCC3_AMENDMENT, ...), a different enum from the lifecycle FilingType. */
export type FeeFilingType = JurisdictionComponents['schemas']['FilingType'];

/** The Badge primitive variants a status badge on these pages renders in. */
type BadgeVariantState = 'success' | 'warning' | 'error' | 'neutral';

/** What a label lookup prints for a value outside its enum. */
export const UNRECOGNIZED_LABEL = 'Unrecognized';

// ---------------------------------------------------------------------------
// Label and variant maps
// ---------------------------------------------------------------------------

export const FILING_STATUS_LABEL: Record<FilingStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  rejected: 'Rejected',
  acknowledged: 'Acknowledged',
  active: 'Active',
  amended: 'Amended',
  continued: 'Continued',
  terminated: 'Terminated',
  lapsed: 'Lapsed',
  imported: 'Imported',
};

export const FILING_STATUS_VARIANT: Record<FilingStatus, BadgeVariantState> = {
  draft: 'warning',
  submitted: 'warning',
  rejected: 'error',
  acknowledged: 'success',
  active: 'success',
  amended: 'neutral',
  continued: 'neutral',
  terminated: 'neutral',
  lapsed: 'warning',
  imported: 'neutral',
};

export const FILING_TYPE_LABEL: Record<FilingType, string> = {
  ucc1: 'UCC-1',
  ucc3Amendment: 'UCC-3 Amendment',
  ucc3Continuation: 'UCC-3 Continuation',
  ucc3Termination: 'UCC-3 Termination',
};

export const FEE_FILING_TYPE_LABEL: Record<FeeFilingType, string> = {
  UCC1: 'UCC-1',
  UCC3_AMENDMENT: 'UCC-3 Amendment',
  UCC3_CONTINUATION: 'UCC-3 Continuation',
  UCC3_TERMINATION: 'UCC-3 Termination',
  UCC3_ASSIGNMENT: 'UCC-3 Assignment',
};

export const FEE_PAYMENT_STATUS_LABEL: Record<FeePaymentStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  reconciled: 'Reconciled',
  refundPending: 'Refund pending',
  refunded: 'Refunded',
  disputed: 'Disputed',
};

export const FEE_PAYMENT_STATUS_VARIANT: Record<FeePaymentStatus, BadgeVariantState> = {
  pending: 'warning',
  confirmed: 'neutral',
  reconciled: 'success',
  refundPending: 'warning',
  refunded: 'neutral',
  disputed: 'error',
};

export const SUBMISSION_STATUS_LABEL: Record<SubmissionStatus, string> = {
  queued: 'Queued',
  submitting: 'Submitting',
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  rejected: 'Rejected',
  failed: 'Failed',
  retrying: 'Retrying',
  permanently_failed: 'Permanently failed',
};

export const SUBMISSION_STATUS_VARIANT: Record<SubmissionStatus, BadgeVariantState> = {
  queued: 'warning',
  submitting: 'warning',
  submitted: 'warning',
  acknowledged: 'success',
  rejected: 'error',
  failed: 'error',
  retrying: 'warning',
  permanently_failed: 'error',
};

export const PAPER_FILING_STATUS_LABEL: Record<PaperFilingStatus, string> = {
  GENERATED: 'Generated',
  QUEUED_FOR_PRINT: 'Queued for print',
  PRINTED: 'Printed',
  MAILED: 'Mailed',
  DELIVERED: 'Delivered',
  ACKNOWLEDGED: 'Acknowledged',
  RETURNED_REJECTED: 'Returned rejected',
};

export const PAPER_FILING_STATUS_VARIANT: Record<PaperFilingStatus, BadgeVariantState> = {
  GENERATED: 'neutral',
  QUEUED_FOR_PRINT: 'neutral',
  PRINTED: 'neutral',
  MAILED: 'warning',
  DELIVERED: 'warning',
  ACKNOWLEDGED: 'success',
  RETURNED_REJECTED: 'error',
};

export const AUDIT_EVENT_TYPE_LABEL: Record<AuditEventType, string> = {
  created: 'Created',
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  rejected: 'Rejected',
  amended: 'Amended',
  continued: 'Continued',
  terminated: 'Terminated',
  lapsed: 'Lapsed',
  searched: 'Searched',
  fee_paid: 'Fee paid',
  fee_refunded: 'Fee refunded',
  exported: 'Exported',
};

export const AUDIT_EVENT_TYPE_VARIANT: Record<AuditEventType, BadgeVariantState> = {
  created: 'neutral',
  submitted: 'warning',
  acknowledged: 'success',
  rejected: 'error',
  amended: 'neutral',
  continued: 'neutral',
  terminated: 'neutral',
  lapsed: 'warning',
  searched: 'neutral',
  fee_paid: 'neutral',
  fee_refunded: 'neutral',
  exported: 'neutral',
};

export const AUDIT_ACTOR_TYPE_LABEL: Record<AuditActorType, string> = {
  system: 'System',
  operator: 'Operator',
  fi_webhook: 'FI webhook',
  scheduler: 'Scheduler',
};

export const COMPLIANCE_REPORT_TYPE_LABEL: Record<ComplianceReportType, string> = {
  filing_coverage: 'Filing coverage',
  rejection_rate: 'Rejection rate',
  continuation_compliance: 'Continuation compliance',
  jurisdiction_metrics: 'Jurisdiction metrics',
};

export const BULK_OPERATION_TYPE_LABEL: Record<BulkOperationType, string> = {
  continuation: 'Continuation',
  amendment: 'Amendment',
  termination: 'Termination',
  import: 'Import',
};

export const BULK_OPERATION_STATUS_LABEL: Record<BulkOperationStatus, string> = {
  pending: 'Pending',
  validating: 'Validating',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  partiallyCompleted: 'Partially completed',
};

export const BULK_OPERATION_STATUS_VARIANT: Record<BulkOperationStatus, BadgeVariantState> = {
  pending: 'warning',
  validating: 'warning',
  processing: 'warning',
  completed: 'success',
  failed: 'error',
  partiallyCompleted: 'warning',
};

export const SEARCH_LOGIC_TYPE_LABEL: Record<SearchLogicType, string> = {
  exact: 'Exact match',
  similar: 'Similar name',
  variant: 'Name variants',
};

export const SUBMISSION_METHOD_LABEL: Record<SubmissionMethod, string> = {
  electronic: 'Electronic',
  paper: 'Paper',
  both: 'Electronic and paper',
};

/** Select options derived from a label map, in the map's declaration order. */
export function optionsFrom<K extends string>(
  labels: Record<K, string>,
): Array<{ value: K; label: string }> {
  return (Object.keys(labels) as K[]).map((value) => ({ value, label: labels[value] }));
}

function hasCode<K extends string>(labels: Record<K, string>, code: string): code is K {
  return Object.prototype.hasOwnProperty.call(labels, code);
}

/** The display label for a code: the mapped label, `--` when absent, "Unrecognized" when outside the enum. */
export function labelFor<K extends string>(
  labels: Record<K, string>,
  code: string | null | undefined,
): string {
  if (code === null || code === undefined || code === '') return ABSENT;
  return hasCode(labels, code) ? labels[code] : UNRECOGNIZED_LABEL;
}

/** A status badge read through a label map and a variant map. */
export function CodeBadge<K extends string>({
  code,
  labels,
  variants,
}: {
  code: string | null | undefined;
  labels: Record<K, string>;
  variants?: Record<K, BadgeVariantState>;
}) {
  const known = typeof code === 'string' && hasCode(labels, code);
  const variant: BadgeVariantState = known && variants ? variants[code as K] : 'neutral';
  return (
    <Badge variant={variant} size="sm">
      <span title={known || !code ? undefined : `Received code: ${code}`}>
        {labelFor(labels, code)}
      </span>
    </Badge>
  );
}

/** First eight characters of an identifier, the form the UCC tables print. */
export function shortId(id: string | null | undefined): string {
  return id ? `${id.slice(0, 8)}...` : ABSENT;
}

// ---------------------------------------------------------------------------
// Read-state chrome
// ---------------------------------------------------------------------------

/** A read that failed, said in words, with an optional retry. */
export function UccReadFailure({
  title,
  error,
  onRetry,
  children,
  className = 'p-4',
}: {
  title: string;
  error: Error;
  onRetry?: () => void;
  children?: ReactNode;
  /** Wrapper classes; the default insets the notice inside a card. */
  className?: string;
}) {
  return (
    <div className={className}>
      <Alert type="error" title={title}>
        <p>{error.message}</p>
        {children}
        {onRetry && (
          <div className="mt-3">
            <Button variant="secondary" size="sm" icon={RefreshIcon} iconPosition="left" onClick={onRetry}>
              Retry
            </Button>
          </div>
        )}
      </Alert>
    </div>
  );
}

export interface UccTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  rows: T[];
  isLoading: boolean;
  error: Error | null | undefined;
  /** Heading of the failure notice, e.g. "Could not load fee payments". */
  errorTitle: string;
  onRetry?: () => void;
  /** What is absent and why. Rendered outside the table so a phone viewport never clips it. */
  empty: { title: string; description?: string; action?: ReactNode };
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  /**
   * Surface classes for the grid and its loading skeleton. The default gives a
   * bare grid its own surface, as the Tenants and Pledges lists do; a grid
   * under a FilterBar passes `rounded-b-sm shadow-sm`, and one inside a Card
   * passes an empty string.
   */
  className?: string;
  /** Server-side paging: the page, size and total the service reported. Omitted, the grid pages client-side. */
  pagination?: PaginationState;
  /** Page sizes offered by the rows-per-page control. */
  pageSizeOptions?: number[];
}

/** Surface classes a bare UCC grid takes. */
export const UCC_GRID_SURFACE = 'rounded-sm shadow-sm';

/**
 * The UCC list body: a skeleton while loading, the failure in words when the
 * read failed, an EmptyState when the read returned no rows, and the DataGrid
 * otherwise. The empty and failure states render outside the grid: inside a
 * colSpan cell of a table wider than a 390 px viewport, the sentence was
 * centered across the table's scroll width and clipped at the card edge.
 *
 * The pages render this bare, not inside a Card: a padded Card inset the grid
 * by 24 px (48 px of a 390 px viewport) and boxed the EmptyState, which is an
 * absence rather than a panel (packages/ui EmptyState).
 */
export function UccTable<T>({
  columns,
  rows,
  isLoading,
  error,
  errorTitle,
  onRetry,
  empty,
  getRowId,
  onRowClick,
  className = UCC_GRID_SURFACE,
  pagination,
  pageSizeOptions,
}: UccTableProps<T>) {
  if (isLoading) {
    return (
      <div className={`bg-surface ${className}`}>
        <SkeletonTable rows={5} columns={Math.min(columns.length, 6)} className="p-4" />
      </div>
    );
  }
  if (error) {
    return <UccReadFailure title={errorTitle} error={error} onRetry={onRetry} className="" />;
  }
  if (rows.length === 0) {
    return <EmptyState title={empty.title} description={empty.description} action={empty.action} />;
  }
  return (
    <DataGrid<T>
      data={rows}
      columns={columns}
      getRowId={getRowId}
      onRowClick={onRowClick}
      pagination={pagination}
      pageSizeOptions={pageSizeOptions}
      hoverable
      className={`w-full ${className}`}
    />
  );
}

/**
 * Classes for a text control inside a grid cell: primary ink (red is kept for
 * CTAs and alerts), an underline on hover, and the selection focus ring. The
 * control gives a row's destination a keyboard and screen-reader target (a
 * DataGrid row click was mouse-only, r3 op-g5-4); DataGrid ignores a row
 * click that starts on a link or button, so the two never both fire.
 */
export const CELL_CONTROL_CLASS =
  'rounded-sm text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection';

/** The filing number as a link to the filing's detail page, keyboard-reachable from any filing list. */
export function FilingNumberLink({ filing }: { filing: Pick<UccFiling, 'filingId' | 'filingNumber'> }) {
  return (
    <Link to={`/ucc/filings/${filing.filingId}`} className={`text-sm font-sans font-medium ${CELL_CONTROL_CLASS}`}>
      {filing.filingNumber || 'Not yet assigned'}
    </Link>
  );
}

/** Loading placeholders for a row of metric cards, the shape the Pledges page uses. */
export function MetricSkeletons({ count, className = '' }: { count: number; className?: string }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          data-testid="metric-skeleton"
          className={`bg-surface p-6 rounded-sm shadow-sm ${i === count - 1 && count % 2 === 1 ? 'col-span-2 md:col-span-1' : ''} ${className}`}
        >
          <Skeleton className="h-3 w-24 mb-3" />
          <Skeleton className="h-8 w-12" />
        </div>
      ))}
    </>
  );
}

/**
 * The metric-row grid for the UCC pages: two cards per row at phone width,
 * the full row at md. With an odd count the last card spans both phone
 * columns, so a five-card row does not leave one card alone on a third row.
 */
export const UCC_METRIC_GRID = 'grid grid-cols-2 gap-4 mb-2';

export function metricGridClass(count: number): string {
  // Five cards fill one md row; eight fill two rows of four.
  const md = count === 5 ? 'md:grid-cols-5' : count === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4';
  return `${UCC_METRIC_GRID} ${md}`;
}

/** The class a metric card at `index` takes in a row of `count`. */
export function metricCellClass(index: number, count: number): string | undefined {
  return count % 2 === 1 && index === count - 1 ? 'col-span-2 md:col-span-1' : undefined;
}

// ---------------------------------------------------------------------------
// Portfolio counts (Dashboard and Filings)
// ---------------------------------------------------------------------------

/**
 * The one label set for the PortfolioPerfectionStatus counts. The Dashboard
 * and Filings cards and the Analytics Portfolio Perfection block read it, so
 * a count carries one name on every page (Analytics printed "Expiring within
 * 90 days" where the Dashboard printed "Expiring (90 days)").
 */
export const PORTFOLIO_COUNT_LABEL = {
  totalFilings: 'Total filings',
  perfectedCount: 'Perfected',
  pendingCount: 'Pending',
  lapsedCount: 'Lapsed',
  rejectedCount: 'Rejected',
  terminatedCount: 'Terminated',
  expiringWithin90Days: 'Expiring (90 days)',
  expiringWithin180Days: 'Expiring (180 days)',
} as const satisfies Partial<Record<keyof PortfolioPerfectionStatus, string>>;

/** Every portfolio count, in the order the Analytics Portfolio Perfection block prints them. */
export const PORTFOLIO_DETAIL_FIELDS = [
  'totalFilings',
  'perfectedCount',
  'pendingCount',
  'lapsedCount',
  'rejectedCount',
  'terminatedCount',
  'expiringWithin90Days',
  'expiringWithin180Days',
] as const satisfies ReadonlyArray<keyof typeof PORTFOLIO_COUNT_LABEL>;

/** The headline counts the Dashboard and Filings pages print. */
const PORTFOLIO_HEADLINE_FIELDS = [
  'totalFilings',
  'perfectedCount',
  'pendingCount',
  'expiringWithin90Days',
  'rejectedCount',
] as const satisfies ReadonlyArray<keyof typeof PORTFOLIO_COUNT_LABEL>;

/**
 * The headline filing counts, read from the tenant's latest portfolio
 * perfection summary (GET /ucc-analytics/perfection-status).
 *
 * The Dashboard and the Filings list each counted statuses over their first
 * page of filings (100 rows and 20 rows), with different expiry windows (30
 * days and 180) and different labels for the same pending set, while calling
 * the result a total. Both now read the one aggregate the ucc-filing service
 * computes over every filing, with one label set and one window. The summary
 * is a snapshot, so its time is printed beside it.
 */
export function UccPortfolioMetrics({ tenantId }: { tenantId: string }) {
  const { data, isLoading, error } = usePerfectionStatus({ tenantId });
  const summary = data?.data?.[0];
  const count = PORTFOLIO_HEADLINE_FIELDS.length;

  let caption: string | undefined;
  if (error) caption = METRIC_READ_CAPTIONS.failed;
  else if (!summary) caption = METRIC_READ_CAPTIONS.notReported;

  let note: string;
  if (error) note = `The portfolio summary could not be read: ${error.message}`;
  else if (summary) {
    note = `Portfolio summary as of ${formatDateTime(summary.summaryGeneratedAt)}. Perfected counts acknowledged and active filings; pending counts draft and submitted filings.`;
  } else {
    note =
      'No portfolio summary has been generated for this tenant yet, so these counts are not available. The UCC filing service writes one when its analytics snapshot refresh runs.';
  }

  return (
    <section aria-label="Portfolio filing counts" className="mb-6">
      <div className={metricGridClass(count)}>
        {isLoading ? (
          <MetricSkeletons count={count} />
        ) : (
          PORTFOLIO_HEADLINE_FIELDS.map((field, i) => (
            <MetricCard
              key={field}
              label={PORTFOLIO_COUNT_LABEL[field]}
              value={summary && !caption ? formatCount(summary[field]) : ABSENT}
              caption={caption}
              className={metricCellClass(i, count)}
            />
          ))
        )}
      </div>
      {!isLoading && (
        <BodyText variant="ui" className="text-sm text-tertiary">
          {note}
        </BodyText>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tenant scope
// ---------------------------------------------------------------------------

/**
 * What the tenant line says the tenant scopes, by what the page shows:
 * metric pages print figures, the Entities and Audit Trail pages print lists,
 * and a search is recorded under the tenant that ran it (UccSearch.tenantId
 * in schema-registry/ucc-search.openapi.yaml) while its results come from the
 * jurisdiction's filing index.
 */
export const TENANT_SCOPE_SENTENCE = {
  figures: 'Every figure on this page covers this tenant only.',
  lists: 'Lists on this page cover this tenant only.',
  searches: 'Searches run from this page are recorded under this tenant.',
} as const;

/**
 * Names the tenant a UCC page reads. The UCC pages scope every query to the
 * signed-in operator's own tenant and offer no tenant selector, so without
 * this line a count for one tenant read as the platform view. Whether the
 * operator surface gains a tenant selector or explicit-tenant routes is an
 * open product ruling; until then the page says what it shows.
 */
export function UccTenantScope({
  tenantId,
  subject = 'figures',
}: {
  tenantId: string;
  /** What the page shows under the tenant; picks the sentence from TENANT_SCOPE_SENTENCE. */
  subject?: keyof typeof TENANT_SCOPE_SENTENCE;
}) {
  // The name comes from the tenant list, not GET /tenants/{id}: a session
  // tenant with no institution record (the E2E tenant) answers that route 404,
  // which put a failed request on every UCC page. Absent from the list, the
  // line names the id alone.
  const { data } = useTenants();
  if (!tenantId) {
    return (
      <Alert type="warning" title="No tenant on this session" className="mb-6">
        Your session carries no tenant, and the UCC services read one tenant at a time, so this page has
        nothing to read.
      </Alert>
    );
  }
  const name = data?.data.find((t) => t.id === tenantId)?.name;
  return (
    <BodyText variant="ui" className="text-sm text-tertiary mb-6">
      Tenant: {name ?? tenantId}. {TENANT_SCOPE_SENTENCE[subject]}
    </BodyText>
  );
}
